import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import styles from './WholesaleInventory.module.css';
import { Plus, Edit3, Trash2 } from 'react-feather';

const ProductModal = ({ item, onClose, onSave }) => {
  const [formData, setFormData] = useState({ 
    name: '', 
    origin: '', 
    process: '', 
    description: '', 
    price: '', 
    unit: '1 KG', 
    category: 'Espresso',
    imageUrl: '',
    stockQuantity: 0,
    minimumStock: 5,
    costPrice: 0,
    sku: '',
    supplier: '',
    notes: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imageInputMethod, setImageInputMethod] = useState('url'); // 'url' or 'upload'
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || '',
        origin: item.origin || '',
        process: item.process || '',
        description: item.description || '',
        price: item.price || '',
        unit: item.unit || '1 KG',
        category: item.category || 'Espresso',
        imageUrl: item.imageUrl || '',
        stockQuantity: item.stockQuantity || 0,
        minimumStock: item.minimumStock || 5,
        costPrice: item.costPrice || 0,
        sku: item.sku || '',
        supplier: item.supplier || '',
        notes: item.notes || ''
      });
      setImageInputMethod('url');
      setImageFile(null);
    } else {
      setFormData({ 
        name: '', 
        origin: '', 
        process: '', 
        description: '', 
        price: '', 
        unit: '1 KG', 
        category: 'Espresso', 
        imageUrl: '',
        stockQuantity: 0,
        minimumStock: 5,
        costPrice: 0,
        sku: '',
        supplier: '',
        notes: ''
      });
      setImageInputMethod('url');
      setImageFile(null);
    }
  }, [item]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.price) {
      setError('Product Name and Price are required.');
      return;
    }
    
    setIsSaving(true);
    setError(null);

    try {
      let finalImageUrl = formData.imageUrl;
      if (imageInputMethod === 'upload' && imageFile) {
        const storageRef = ref(storage, `master_wholesale_products/${Date.now()}_${imageFile.name}`);
        const uploadTask = await uploadBytes(storageRef, imageFile);
        finalImageUrl = await getDownloadURL(uploadTask.ref);
      }
      
      const docData = { ...formData, imageUrl: finalImageUrl };
      onSave(docData, item ? item.id : null);

    } catch (err) {
      setError('Failed to save product.');
      console.error(err);
    }
    setIsSaving(false);
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <div className={styles.modalTitle}>
              <h2>{item ? 'Edit Master Product' : 'Create Master Product'}</h2>
              <p className={styles.modalSubtitle}>
                {item ? 'Update product information and inventory details' : 'Add a new product to your wholesale inventory'}
              </p>
            </div>
            <button className={styles.closeButton} onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          
          <div className={styles.modalBody}>
            <form onSubmit={handleSubmit} className={styles.form}>
              {/* Product Information Section */}
              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>Product Information</h3>
                <div className={styles.formGrid}>
                  <div className={styles.formGroup}>
                    <label>Product Name *</label>
                    <input 
                      name="name" 
                      value={formData.name} 
                      onChange={handleInputChange} 
                      placeholder="Ethiopian Yirgacheffe"
                      required 
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>SKU</label>
                    <input 
                      name="sku" 
                      value={formData.sku} 
                      onChange={handleInputChange} 
                      placeholder="ETH-YIR-001"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Origin</label>
                    <input 
                      name="origin" 
                      value={formData.origin} 
                      onChange={handleInputChange} 
                      placeholder="Ethiopia"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Process</label>
                    <input 
                      name="process" 
                      value={formData.process} 
                      onChange={handleInputChange} 
                      placeholder="Washed"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Category</label>
                    <select name="category" value={formData.category} onChange={handleInputChange}>
                      <option value="Espresso">Espresso</option>
                      <option value="Filter">Filter</option>
                      <option value="Both">Both</option>
                    </select>
                  </div>
                  <div className={styles.formGroup}>
                    <label>Unit</label>
                    <input 
                      name="unit" 
                      value={formData.unit} 
                      onChange={handleInputChange} 
                      placeholder="1 KG"
                    />
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label>Description</label>
                  <textarea 
                    name="description" 
                    value={formData.description} 
                    onChange={handleInputChange} 
                    placeholder="Bright and floral coffee with citrus notes..."
                    rows="3"
                  />
                </div>
              </div>

              {/* Pricing Section */}
              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>Pricing</h3>
                <div className={styles.formGrid}>
                  <div className={styles.formGroup}>
                    <label>Selling Price (QAR) *</label>
                    <input 
                      type="number" 
                      name="price" 
                      value={formData.price} 
                      onChange={handleInputChange} 
                      placeholder="85.00"
                      required 
                      step="0.01" 
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Cost Price (QAR)</label>
                    <input 
                      type="number" 
                      name="costPrice" 
                      value={formData.costPrice} 
                      onChange={handleInputChange} 
                      placeholder="60.00"
                      step="0.01" 
                    />
                  </div>
                </div>
              </div>

              {/* Inventory Section */}
              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>Inventory Management</h3>
                <div className={styles.formGrid}>
                  <div className={styles.formGroup}>
                    <label>Current Stock</label>
                    <input 
                      type="number" 
                      name="stockQuantity" 
                      value={formData.stockQuantity} 
                      onChange={handleInputChange} 
                      placeholder="25"
                      min="0" 
                      step="1" 
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Minimum Stock Alert</label>
                    <input 
                      type="number" 
                      name="minimumStock" 
                      value={formData.minimumStock} 
                      onChange={handleInputChange} 
                      placeholder="5"
                      min="0" 
                      step="1" 
                    />
                  </div>
                </div>
              </div>

              {/* Supplier Section */}
              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>Supplier Information</h3>
                <div className={styles.formGroup}>
                  <label>Supplier</label>
                  <input 
                    name="supplier" 
                    value={formData.supplier} 
                    onChange={handleInputChange} 
                    placeholder="Ethiopian Coffee Traders"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Notes</label>
                  <textarea 
                    name="notes" 
                    value={formData.notes} 
                    onChange={handleInputChange} 
                    placeholder="Additional notes about this product..."
                    rows="3"
                  />
                </div>
              </div>

              {/* Image Section */}
              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>Product Image</h3>
                <div className={styles.imageSection}>
                  <div className={styles.imageInputToggle}>
                    <button 
                      type="button" 
                      className={`${styles.toggleButton} ${imageInputMethod === 'url' ? styles.active : ''}`} 
                      onClick={() => setImageInputMethod('url')}
                    >
                      URL
                    </button>
                    <button 
                      type="button" 
                      className={`${styles.toggleButton} ${imageInputMethod === 'upload' ? styles.active : ''}`} 
                      onClick={() => setImageInputMethod('upload')}
                    >
                      Upload
                    </button>
                  </div>
                  {imageInputMethod === 'url' ? (
                    <input 
                      type="text" 
                      name="imageUrl" 
                      value={formData.imageUrl} 
                      onChange={handleInputChange} 
                      placeholder="https://images.unsplash.com/photo-1447933601403-0c6688de566e"
                    />
                  ) : (
                    <input 
                      type="file" 
                      onChange={handleFileChange} 
                      accept="image/png, image/jpeg, image/webp"
                      className={styles.fileInput}
                    />
                  )}
                  {(formData.imageUrl || imageFile) && (
                    <div className={styles.imagePreview}>
                      <img 
                        src={formData.imageUrl || (imageFile ? URL.createObjectURL(imageFile) : '')} 
                        alt="Product preview" 
                        className={styles.previewImage}
                      />
                    </div>
                  )}
                </div>
              </div>

              {error && <div className={styles.errorMessage}>{error}</div>}
            </form>
          </div>

          <div className={styles.modalFooter}>
            <button type="button" className={styles.cancelButton} onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className={styles.saveButton} 
              disabled={isSaving}
              onClick={handleSubmit}
            >
              {isSaving ? (
                <>
                  <svg className={styles.spinner} width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeOpacity="0.3"/>
                    <path d="M12 2 A10 10 0 0 1 22 12" stroke="currentColor" strokeWidth="4" strokeDasharray="31.416" strokeDashoffset="31.416"/>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                    <polyline points="17,21 17,13 7,13 7,21"/>
                    <polyline points="7,3 7,8 15,8"/>
                  </svg>
                  Save Product
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const WholesaleInventory = () => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [stockAlerts, setStockAlerts] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalProducts: 0,
    lowStockItems: 0,
    totalValue: 0,
    categoriesCount: {}
  });
  const { currentUser } = useAuth();

  const productsCollectionRef = collection(db, 'wholesaleProducts');

  useEffect(() => {
    const fetchInventory = async () => {
      // Check if user is authenticated
      if (!currentUser) {
        setError('Please log in to access the inventory.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        console.log('Fetching inventory for user:', currentUser.uid);
        const snapshot = await getDocs(collection(db, 'wholesaleProducts'));
        console.log('Snapshot received:', snapshot.size, 'documents');
        
        const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        console.log('Items processed:', items.length);
        
        setInventory(items);
        
        // Calculate analytics
        const totalProducts = items.length;
        const lowStockItems = items.filter(item => (item.stockQuantity || 0) <= (item.minimumStock || 5)).length;
        const totalValue = items.reduce((sum, item) => sum + ((item.price || 0) * (item.stockQuantity || 0)), 0);
        const categoriesCount = items.reduce((acc, item) => {
          const category = item.category || 'Uncategorized';
          acc[category] = (acc[category] || 0) + 1;
          return acc;
        }, {});
        
        setAnalytics({ totalProducts, lowStockItems, totalValue, categoriesCount });
        
        // Set stock alerts
        const alerts = items.filter(item => (item.stockQuantity || 0) <= (item.minimumStock || 5));
        setStockAlerts(alerts);
        
      } catch (err) {
        console.error('Error fetching inventory:', err);
        console.error('Error code:', err.code);
        console.error('Error message:', err.message);
        
        // Provide more specific error messages
        let errorMessage = 'Failed to fetch inventory';
        if (err.code === 'permission-denied') {
          errorMessage = 'Permission denied. Please check Firestore security rules.';
        } else if (err.code === 'unavailable') {
          errorMessage = 'Database is unavailable. Please try again later.';
        } else if (err.message) {
          errorMessage = `Failed to fetch inventory: ${err.message}`;
        }
        
        setError(errorMessage);
        // Set default values when error occurs
        setInventory([]);
        setAnalytics({ totalProducts: 0, lowStockItems: 0, totalValue: 0, categoriesCount: {} });
        setStockAlerts([]);
      }
      setLoading(false);
    };
    
    if (currentUser) {
      fetchInventory();
    }
  }, [currentUser]);

  const handleSaveProduct = async (formData, itemId) => {
    try {
      if (itemId) {
        const itemRef = doc(db, 'wholesaleProducts', itemId);
        await updateDoc(itemRef, formData);
        setInventory(prev => prev.map(item => item.id === itemId ? { id: itemId, ...formData } : item));
      } else {
        const docRef = await addDoc(productsCollectionRef, formData);
        setInventory(prev => [...prev, { id: docRef.id, ...formData }]);
      }
      setIsModalOpen(false);
      setEditingItem(null);
      setError(null);
    } catch (err) {
      console.error('Error saving product:', err);
      setError(`Failed to save product: ${err.message}`);
    }
  };
  
  const handleDeleteProduct = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this master product? This cannot be undone.')) return;
    
    const itemRef = doc(db, 'wholesaleProducts', itemId);
    await deleteDoc(itemRef);
    setInventory(prev => prev.filter(item => item.id !== itemId));
  };
  
  const handleOpenModal = (item = null) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleUpdateStock = async (itemId, newQuantity) => {
    try {
      const itemRef = doc(db, 'wholesaleProducts', itemId);
      await updateDoc(itemRef, { stockQuantity: newQuantity });
      setInventory(prev => prev.map(item => 
        item.id === itemId ? { ...item, stockQuantity: newQuantity } : item
      ));
    } catch (err) {
      setError('Failed to update stock.');
      console.error(err);
    }
  };

  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.origin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.sku?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'All' || item.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const exportInventory = () => {
    const csvContent = [
      ['Name', 'SKU', 'Category', 'Origin', 'Process', 'Stock Quantity', 'Minimum Stock', 'Unit', 'Price', 'Cost Price', 'Supplier', 'Status'],
      ...filteredInventory.map(item => [
        item.name || '',
        item.sku || '',
        item.category || '',
        item.origin || '',
        item.process || '',
        item.stockQuantity || 0,
        item.minimumStock || 0,
        item.unit || '',
        item.price || 0,
        item.costPrice || 0,
        item.supplier || '',
        (item.stockQuantity || 0) <= (item.minimumStock || 5) ? 'Low Stock' : 'In Stock'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wholesale-inventory-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const createSampleData = async () => {
    try {
      const sampleProducts = [
        {
          name: 'Ethiopian Yirgacheffe',
          origin: 'Ethiopia',
          process: 'Washed',
          description: 'Bright and floral coffee with citrus notes',
          price: 85,
          costPrice: 60,
          unit: '1 KG',
          category: 'Filter',
          stockQuantity: 25,
          minimumStock: 10,
          sku: 'ETH-YIR-001',
          supplier: 'Ethiopian Coffee Traders',
          notes: 'Premium grade coffee beans',
          imageUrl: 'https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=400'
        },
        {
          name: 'Colombian Supremo',
          origin: 'Colombia',
          process: 'Natural',
          description: 'Rich and full-bodied with chocolate notes',
          price: 78,
          costPrice: 55,
          unit: '1 KG',
          category: 'Espresso',
          stockQuantity: 3,
          minimumStock: 5,
          sku: 'COL-SUP-001',
          supplier: 'Colombian Coffee Co.',
          notes: 'Great for espresso blends',
          imageUrl: 'https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=400'
        },
        {
          name: 'Brazilian Santos',
          origin: 'Brazil',
          process: 'Pulped Natural',
          description: 'Smooth and nutty with low acidity',
          price: 72,
          costPrice: 50,
          unit: '1 KG',
          category: 'Both',
          stockQuantity: 15,
          minimumStock: 8,
          sku: 'BRA-SAN-001',
          supplier: 'Brazilian Coffee Exports',
          notes: 'Versatile for all brewing methods',
          imageUrl: 'https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=400'
        }
      ];

      for (const product of sampleProducts) {
        await addDoc(productsCollectionRef, product);
      }
      
      // Refresh the inventory after adding sample data
      const snapshot = await getDocs(collection(db, 'wholesaleProducts'));
      const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setInventory(items);
      setError(null);
      
    } catch (err) {
      console.error('Error creating sample data:', err);
      setError(`Failed to create sample data: ${err.message}`);
    }
  };
  
  return (
    <div className={styles.pageContainer}>
      {/* Analytics Header */}
      <div className={styles.analyticsSection}>
        <div className={styles.analyticsGrid}>
          <div className={styles.analyticsCard}>
            <div className={styles.analyticsValue}>{analytics.totalProducts}</div>
            <div className={styles.analyticsLabel}>Total Products</div>
          </div>
          <div className={styles.analyticsCard}>
            <div className={styles.analyticsValue}>{analytics.lowStockItems}</div>
            <div className={styles.analyticsLabel}>Low Stock Items</div>
          </div>
          <div className={styles.analyticsCard}>
            <div className={styles.analyticsValue}>QAR {analytics.totalValue.toLocaleString()}</div>
            <div className={styles.analyticsLabel}>Total Inventory Value</div>
          </div>
          <div className={styles.analyticsCard}>
            <div className={styles.analyticsValue}>{Object.keys(analytics.categoriesCount).length}</div>
            <div className={styles.analyticsLabel}>Categories</div>
          </div>
        </div>
      </div>

      {/* Stock Alerts */}
      {stockAlerts.length > 0 && (
        <div className={styles.alertsSection}>
          <h3>Stock Alerts</h3>
          <div className={styles.alertsList}>
            {stockAlerts.map(item => (
              <div key={item.id} className={styles.alertItem}>
                <span>{item.name} - Stock: {item.stockQuantity || 0} (Min: {item.minimumStock || 5})</span>
                <button 
                  className={styles.updateStockButton}
                  onClick={() => {
                    const newStock = prompt(`Update stock for ${item.name}:`, item.stockQuantity || 0);
                    if (newStock !== null && !isNaN(newStock)) {
                      handleUpdateStock(item.id, parseInt(newStock));
                    }
                  }}
                >
                  Update Stock
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles.header}>
        <h1>Wholesale Inventory</h1>
        <div className={styles.headerActions}>
          <button className={styles.exportButton} onClick={exportInventory}>Export CSV</button>
          <button className={styles.addButton} onClick={() => handleOpenModal()}><Plus size={16}/> Add New Product</button>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className={styles.controlsSection}>
        <div className={styles.searchGroup}>
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="All">All Categories</option>
            <option value="Espresso">Espresso</option>
            <option value="Filter">Filter</option>
            <option value="Both">Both</option>
          </select>
        </div>
      </div>

      {loading && <p>Loading inventory...</p>}
      {error && (
        <div className={styles.error}>
          <p>{error}</p>
          <button 
            className={styles.retryButton} 
            onClick={() => window.location.reload()}
            style={{ marginTop: '8px' }}
          >
            Retry
          </button>
        </div>
      )}

      <div className={styles.tableContainer}>
        <table>
          <thead>
            <tr>
              <th>Image</th>
              <th>Name / SKU</th>
              <th>Category</th>
              <th>Origin</th>
              <th>Stock</th>
              <th>Price</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInventory.map(item => (
              <tr key={item.id} className={(item.stockQuantity || 0) <= (item.minimumStock || 5) ? styles.lowStockRow : ''}>
                <td>
                  <img src={item.imageUrl || 'https://via.placeholder.com/40'} alt={item.name} className={styles.itemImage}/>
                </td>
                <td>
                  <div className={styles.nameCell}>
                    <div className={styles.itemName}>{item.name}</div>
                    {item.sku && <div className={styles.itemSku}>SKU: {item.sku}</div>}
                  </div>
                </td>
                <td>
                  <span className={`${styles.categoryBadge} ${styles[`category${item.category}`]}`}>
                    {item.category}
                  </span>
                </td>
                <td>{item.origin || '-'}</td>
                <td>
                  <div className={styles.stockCell}>
                    <span className={styles.stockQuantity}>{item.stockQuantity || 0}</span>
                    <span className={styles.stockUnit}>{item.unit}</span>
                    <button 
                      className={styles.quickUpdateButton}
                      onClick={() => {
                        const newStock = prompt(`Update stock for ${item.name}:`, item.stockQuantity || 0);
                        if (newStock !== null && !isNaN(newStock)) {
                          handleUpdateStock(item.id, parseInt(newStock));
                        }
                      }}
                    >
                      +/-
                    </button>
                  </div>
                </td>
                <td>{Number(item.price || 0).toFixed(2)} QAR</td>
                <td>
                  <span className={`${styles.statusBadge} ${(item.stockQuantity || 0) <= (item.minimumStock || 5) ? styles.lowStock : styles.inStock}`}>
                    {(item.stockQuantity || 0) <= (item.minimumStock || 5) ? 'Low Stock' : 'In Stock'}
                  </span>
                </td>
                <td>
                  <div className={styles.actionButtons}>
                    <button className={styles.iconButton} onClick={() => handleOpenModal(item)}><Edit3 size={16}/></button>
                    <button className={styles.iconButton} onClick={() => handleDeleteProduct(item.id)}><Trash2 size={16}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredInventory.length === 0 && !loading && (
        <div className={styles.emptyState}>
          {inventory.length === 0 ? (
            <div>
              <p>No products in inventory yet.</p>
              <button 
                className={styles.addButton} 
                onClick={createSampleData}
                style={{ marginTop: '16px' }}
              >
                Create Sample Data
              </button>
            </div>
          ) : (
            <p>No products found matching your search criteria.</p>
          )}
        </div>
      )}

      {isModalOpen && <ProductModal item={editingItem} onClose={() => setIsModalOpen(false)} onSave={handleSaveProduct} />}
    </div>
  );
};

export default WholesaleInventory; 